import { calculateGridConfigurations } from './grid/calculator.js';
// alignment helpers not needed directly here; used in panel
import { 
  getPresetsForUnit, 
  getPresetsByCategory, 
  findPresetById, 
  getOppositeOrientation 
} from './presets.js';
import { transformToMatrix } from './grid/matrix.js';
import { createPaginatedMatrix } from './ui/matrix-pagination.js';
import { createConfigurationPanel } from './ui/configuration-panel.js';
import './style.css';
import './ui/matrix-styles.css';
import './ui/configuration-panel-styles.css';
import './3-section-layout.css';
import { formatNumber } from './ui/utils.js';

// formatNumber now in utils.js

// Keep matrix from moving under the cursor when details height changes
function stabilizeViewport(updateFn) {
  const anchor = document.getElementById('matrix-display-area') || document.getElementById('matrix-container');
  const beforeTop = anchor ? anchor.getBoundingClientRect().top : null;
  updateFn();
  if (!anchor || beforeTop == null) return;
  requestAnimationFrame(() => {
    const afterTop = anchor.getBoundingClientRect().top;
    const delta = afterTop - beforeTop;
    if (delta !== 0) {
      window.scrollBy(0, delta);
    }
  });
}

// DOM elements
const gridForm = document.getElementById('grid-form');
const matrixContainer = document.getElementById('matrix-container');
const configurationSection = document.getElementById('configuration-details-section');
const unitSelect = document.getElementById('unit');
const presetSelect = document.getElementById('preset');
const rotateButton = document.getElementById('rotate-preset');
const pageWidthInput = document.getElementById('page-width');
const pageHeightInput = document.getElementById('page-height');

// Matrix components state
let currentMatrix = null;
let currentPanel = null;

// Form submission handler
gridForm.addEventListener('submit', (e) => {
  e.preventDefault();
  calculateAndDisplayResults();
});

// Preset functionality
unitSelect.addEventListener('change', updatePresetOptions);
presetSelect.addEventListener('change', applyPreset);
rotateButton.addEventListener('click', rotatePreset);

// Auto-calculate on input change
const inputs = gridForm.querySelectorAll('input, select');
inputs.forEach(input => {
  input.addEventListener('input', debounce(calculateAndDisplayResults, 300));
});

// If user edits dimensions while a preset is selected, auto-switch to custom
function handleDimensionEdit() {
  if (presetSelect.value) {
    presetSelect.value = '';
    updateRotateButtonVisibility();
  }
}

pageWidthInput.addEventListener('input', handleDimensionEdit);
pageHeightInput.addEventListener('input', handleDimensionEdit);

function updatePresetOptions() {
  const unit = unitSelect.value;
  const currentPreset = presetSelect.value;
  
  // Clear existing options except the first one
  presetSelect.innerHTML = '<option value="">Custom Dimensions</option>';
  
  // Group presets by category
  const presetsByCategory = getPresetsByCategory(unit);
  
  // Add options grouped by category
  Object.keys(presetsByCategory).forEach(category => {
    const optgroup = document.createElement('optgroup');
    optgroup.label = category;
    
    presetsByCategory[category].forEach(preset => {
      const option = document.createElement('option');
      option.value = preset.id;
      option.textContent = preset.name;
      option.title = preset.description;
      optgroup.appendChild(option);
    });
    
    presetSelect.appendChild(optgroup);
  });
  
  // Try to maintain selection if switching between units
  if (currentPreset && findPresetById(currentPreset, unit)) {
    presetSelect.value = currentPreset;
  } else {
    presetSelect.value = '';
  }
  
  // Update rotate button visibility
  updateRotateButtonVisibility();
}

function applyPreset() {
  const presetId = presetSelect.value;
  const unit = unitSelect.value;
  
  if (!presetId) {
    // Custom dimensions selected - enable inputs
    pageWidthInput.disabled = false;
    pageHeightInput.disabled = false;
    updateRotateButtonVisibility();
    return;
  }
  
  const preset = findPresetById(presetId, unit);
  if (preset) {
    // Apply preset dimensions
    pageWidthInput.value = preset.width;
    pageHeightInput.value = preset.height;
    // Keep inputs editable; if the user edits, we'll auto-switch to custom
    // Trigger calculation
    calculateAndDisplayResults();
  }
  
  updateRotateButtonVisibility();
}

function rotatePreset() {
  const presetId = presetSelect.value;
  const unit = unitSelect.value;
  
  if (!presetId) {
    // Manual rotation for custom dimensions
    const currentWidth = pageWidthInput.value;
    const currentHeight = pageHeightInput.value;
    pageWidthInput.value = currentHeight;
    pageHeightInput.value = currentWidth;
    calculateAndDisplayResults();
    return;
  }
  
  // Find opposite orientation preset
  const oppositePreset = getOppositeOrientation(presetId, unit);
  if (oppositePreset) {
    presetSelect.value = oppositePreset.id;
    applyPreset();
  }
}

function updateRotateButtonVisibility() {
  const presetId = presetSelect.value;
  const unit = unitSelect.value;
  
  if (!presetId) {
    // Show rotate button for custom dimensions
    rotateButton.style.display = 'inline-block';
    rotateButton.title = 'Swap width and height dimensions';
    return;
  }
  
  // Show rotate button only if opposite orientation exists
  const oppositePreset = getOppositeOrientation(presetId, unit);
  if (oppositePreset) {
    rotateButton.style.display = 'inline-block';
    rotateButton.title = `Switch to ${oppositePreset.name}`;
  } else {
    rotateButton.style.display = 'none';
  }
}

function calculateAndDisplayResults() {
  try {
    // Get form values
    const formData = getFormData();
    
    // Validate inputs
    if (!validateInputs(formData)) {
      return;
    }

    // Show loading state
    showLoadingState();

    // Calculate configurations with a small delay to show loading
    setTimeout(() => {
      try {
        const configurations = calculateGridConfigurations(formData);
        
        // Display results with matrix interface
        displayResults(configurations, formData);
      } catch (error) {
        displayError(error.message);
      }
    }, 200);
    
  } catch (error) {
    displayError(error.message);
  }
}

function getFormData() {
  return {
    unit: document.getElementById('unit').value,
    pageWidth: parseFloat(document.getElementById('page-width').value),
    pageHeight: parseFloat(document.getElementById('page-height').value),
    lineHeight: parseFloat(document.getElementById('line-height').value),
    cols: parseInt(document.getElementById('columns').value),
    rows: parseInt(document.getElementById('rows').value),
    colGutter: parseFloat(document.getElementById('col-gutter').value),
    rowGutter: parseFloat(document.getElementById('row-gutter').value)
  };
}

function validateInputs(data) {
  const errors = [];
  
  if (data.pageWidth <= 0) errors.push('Page width must be positive');
  if (data.pageHeight <= 0) errors.push('Page height must be positive');
  if (data.lineHeight <= 0) errors.push('Line height must be positive');
  if (data.cols <= 0) errors.push('Columns must be positive');
  if (data.rows <= 0) errors.push('Rows must be positive');
  if (data.colGutter < 0) errors.push('Column gutter cannot be negative');
  if (data.rowGutter < 0) errors.push('Row gutter cannot be negative');
  
  if (errors.length > 0) {
    displayError(errors.join(', '));
    return false;
  }
  
  return true;
}

function showLoadingState() {
  matrixContainer.innerHTML = `
    <div class="matrix-loading">Calculating...</div>
  `;
}

function displayResults(configurations, formData) {
  if (configurations.length === 0) {
    displayNoResults();
    return;
  }

  // Transform configurations to matrix
  const matrixData = transformToMatrix(configurations);
  const { dimensions, totalConfigurations } = matrixData;
  
  // Determine if pagination is needed
  const pageSize = 5;
  const needsPagination = dimensions.width > pageSize || dimensions.height > pageSize;
  
  // Display matrix info and matrix component
  displayMatrixSection(matrixData, formData, needsPagination);
  
  // Create configuration panel in section 3
  currentPanel = createConfigurationPanel(configurationSection);
  
  // Track selected and hovered cells; render panel from this state
  let selectedCell = null;
  let hoveredCell = null;

  const renderDetails = () => {
    const pick = hoveredCell ?? selectedCell;
    stabilizeViewport(() => {
      if (pick) {
        const cellData = matrixData.getCellContent(pick.x, pick.y);
        if (cellData) {
          currentPanel.showConfiguration(cellData, formData, { x: pick.x, y: pick.y });
          return;
        }
      }
      currentPanel.showEmpty();
    });
  };

  // Set up selection handler
  currentMatrix.onCellSelect((xIndex, yIndex, cellElement) => {
    if (xIndex == null || yIndex == null) {
      // Deselection
      selectedCell = null;
      // Clear any hover preview so panel becomes empty immediately
      hoveredCell = null;
      renderDetails();
      return;
    }
    const cellData = matrixData.getCellContent(xIndex, yIndex);
    if (cellData) {
      selectedCell = { x: xIndex, y: yIndex };
    }
    renderDetails();
  });

  // Update details on hover as a live preview (does not change selection)
  currentMatrix.onCellHover((xIndex, yIndex, cellElement) => {
    hoveredCell = { x: xIndex, y: yIndex };
    renderDetails();
  });

  // When hover ends, restore the selected cell details (if any)
  currentMatrix.onHoverEnd(() => {
    hoveredCell = null;
    renderDetails();
  });
}

function displayMatrixSection(matrixData, formData, needsPagination) {
  const { dimensions, totalConfigurations } = matrixData;
  // Minimal header (total configurations), then the matrix display
  const matrixInfo = `
    <div class="text-sm text-gray-700 font-medium mb-3">
      <span class="text-green-600 font-bold">${totalConfigurations}</span> configurations
    </div>
    <div id="matrix-display-area"></div>
  `;
  
  matrixContainer.innerHTML = matrixInfo;
  
  // Get the matrix display area
  const matrixDisplayArea = document.getElementById('matrix-display-area');
  
  // Create matrix component
  currentMatrix = createPaginatedMatrix(
    matrixData,
    formData,
    matrixDisplayArea,
    {
      pageSize: 5,
      showPagination: true,
      showStats: false
    }
  );
}


function displayNoResults() {
  matrixContainer.innerHTML = `
    <div class="text-center py-10 px-5 text-gray-500">
      <div class="text-5xl mb-4 opacity-60">⚠️</div>
      <div class="text-base font-semibold mb-2 text-amber-600">No Valid Configurations</div>
      <p class="text-sm leading-relaxed m-0">
        No configurations satisfy the <br>25% margin constraint.
      </p>
      <div class="mt-5 p-3 bg-amber-50 rounded border border-amber-200 text-left text-xs">
        <div class="text-amber-800 font-medium mb-1">💡 Try:</div>
        <div class="text-amber-700">
          • Reduce columns/rows<br>
          • Increase page size<br>
          • Smaller gutters/line height
        </div>
      </div>
    </div>
  `;
  
  // Clear configuration section
  configurationSection.innerHTML = '';
}

function displayError(message) {
  matrixContainer.innerHTML = `
    <div class="text-center py-10 px-5 text-gray-500">
      <div class="text-5xl mb-4 opacity-60">❌</div>
      <div class="text-base font-semibold mb-2 text-red-600">Calculation Error</div>
      <p class="text-sm m-0 font-mono bg-gray-50 p-3 rounded border border-gray-200">${message}</p>
    </div>
  `;
  
  // Clear configuration section
  configurationSection.innerHTML = '';
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initial setup on page load
document.addEventListener('DOMContentLoaded', () => {
  updatePresetOptions();
  calculateAndDisplayResults();
});
